import { useUserWorkspace } from '@common/VerticalApps/hooks';
import { useVerticalAppFlag } from '@common/VerticalApps/VerticalAppContext';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useUser } from 'frontend/core/src/context/user';
import { useRestQuery } from 'frontend/core/src/data/util';
import { useState, useEffect, useMemo } from 'react';

import { DEFAULT_CUSTOMER_BALANCE, DEFAULT_PROFESSIONAL_METERING_INFO } from './constants';
import { getCustomerBalance, getProfessionalMeteringInfo } from './data/api';
import { computeError, ProfessionalEditionCreditsContext } from './ProfessionalEditionCreditsProvider';
import { CustomerBalance, ProfessionalMeteringInfo } from './types';
import { UsageAlertDialog } from './UsageAlertDialog';

const REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

export const ProfessionalEditionCreditsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const { currentWorkspaceId = '' } = useUserWorkspace();

  const isVerticalApp = useVerticalAppFlag();
  const [meteringInfo, setMeteringInfo] = useState<ProfessionalMeteringInfo>(DEFAULT_PROFESSIONAL_METERING_INFO);
  const [customerBalance, setCustomerBalance] = useState<CustomerBalance>(DEFAULT_CUSTOMER_BALANCE);

  const {
    data,
    isLoading: meteringInfoLoading,
    refetch,
    error
  } = useRestQuery<ProfessionalMeteringInfo>(
    ['get-credit-usage', currentWorkspaceId],
    () => getProfessionalMeteringInfo(currentWorkspaceId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isVerticalApp && !!currentWorkspaceId,
      onError: () => {
        return;
      },
      refetchInterval: REFRESH_INTERVAL
    }
  );

  const { data: customerBalanceData, isLoading: customerBalanceLoading } = useRestQuery<CustomerBalance>(
    ['get-customer-balance', user.id, currentWorkspaceId],
    () => getCustomerBalance(user.id, currentWorkspaceId),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isVerticalApp && !!currentWorkspaceId && !!user.id,
      onError: () => {
        return;
      }
    }
  );

  useEffect(() => {
    if (data) {
      setMeteringInfo(data?.data as ProfessionalMeteringInfo);
    }
  }, [data]);

  useEffect(() => {
    if (customerBalanceData) {
      setCustomerBalance(customerBalanceData?.data as CustomerBalance);
    }
  }, [customerBalanceData]);

  const updateMeteringInfo = usePersistentCallback((updates: Partial<ProfessionalMeteringInfo>) => {
    setMeteringInfo((prev) => ({ ...prev, ...updates }));
  });

  const customError = computeError(error);
  const isLoading = meteringInfoLoading || customerBalanceLoading;
  const value = useMemo(
    () => ({
      meteringInfo,
      isLoading,
      refetch,
      error: customError,
      updateMeteringInfo,
      customerBalance,
      customerBalanceLoading
    }),
    [meteringInfo, isLoading, refetch, customError, updateMeteringInfo, customerBalance, customerBalanceLoading]
  );
  return (
    <ProfessionalEditionCreditsContext.Provider value={value}>
      {children} <UsageAlertDialog />
    </ProfessionalEditionCreditsContext.Provider>
  );
};
